import React, { useState, useEffect } from 'react';

// (Sep 2026) Region/authority admin screens — the frontend counterpart to
// authorityRoutes.js's appoint-ga and ra-standins endpoints, which existed
// and passed live tests since 6 Sep but had zero UI. Rendered as a
// Settings tab (rather than new top-level App.js steps) since this is
// account/role configuration, matching how restrictToProfileTabs already
// gates tabs by role — this tab is the same pattern one level up. Shown
// only when raRegions.length > 0, computed server-side in
// landingService.js and passed down as a prop.
//
// appoint-ga is called for BOTH appointing a new GA and adjusting an
// existing one's cap — it upserts server-side (refreshing the cap on
// re-appointment within the same region), so there's deliberately no
// separate "raise cap" form here.
//
// ra-standins takes a raw account id, not an email — GET /api/users/lookup
// resolves the typed email to an id first.
//
// SPLIT 8 SEP 2026 from the original combined AuthorityTab.jsx: that
// version rendered the RA-side forms (this file) and the SA-side
// approval inbox (now SystemAuthorityTab.jsx) on the same page. When one
// account holds both roles for testing (as nmnajida@gmail.com does — SA
// globally, RA of France), seeing both hats on one screen made it genuinely
// unclear whose envelope was being requested from whom. Splitting into two
// tabs, one per authority tier, removes that ambiguity even though in real
// usage an RA and the SA are almost never the same person.

const ROLE_LABEL = { ra: 'Regional Authority', ra_standin: 'RA stand-in' };

// (2 Nov 2026, decision 172) Controls at least 44 points tall on phones, so a finger
// lands on the one it means; wider screens keep their usual size.
const TOUCH = 'min-h-[44px] sm:min-h-0';
// The small buttons beside each Group Admin: Edit cap (N), Remove, Keep?, Remove?
const GA_BTN = `${TOUCH} inline-flex items-center justify-center px-3 sm:px-2 py-1 sm:py-0.5 rounded-lg sm:rounded border text-sm sm:text-xs font-medium transition disabled:opacity-50`;

const RegionalAuthorityTab = ({ apiBaseUrl, authToken, raRegions, onStatusMessage }) => {
  // --- Appoint GA + set cap (per RA region) ---
  const [gaEmailByRegion, setGaEmailByRegion] = useState({});
  const [gaCapByRegion, setGaCapByRegion] = useState({});
  // (28 Oct 2026, decision 151) An address typed in, for someone who is not in the
  // picker (not a member of this region's calendars, or not in Calendar Hub at all).
  // When filled, it is used instead of the picker's choice.
  const [gaTypedByRegion, setGaTypedByRegion] = useState({});
  // (28 Oct 2026, decision 151) "Remove" on a Group Admin who owns no calendars:
  // pressing it shows "Keep?" and "Remove?" in its place (decision 130's pattern).
  const [removeAskId, setRemoveAskId] = useState(null); // roleAssignmentId being asked about
  const [isRemovingGa, setIsRemovingGa] = useState(null);
  const [isAppointingGa, setIsAppointingGa] = useState(null); // regionId currently submitting

  // (Sep 2026) Region member list, feeding the GA dropdown below instead
  // of a free-text email field — avoids typos and picking someone who
  // isn't actually a member of this region's calendars. Fetched once per
  // region on mount.
  const [membersByRegion, setMembersByRegion] = useState({});
  const [isLoadingMembers, setIsLoadingMembers] = useState({});

  // Current Group Admins for each region — appoint-ga was write-only, so
  // there was no visibility into who holds GA or their cap without a
  // direct SQL check. Refetched after a successful appoint/update so the
  // list reflects the change immediately.
  const [gaListByRegion, setGaListByRegion] = useState({});
  const [isLoadingGaList, setIsLoadingGaList] = useState({});

  // (Sep 2026, Amendment 1) Appointed stand-ins per region, feeding the
  // hand-off dropdown — and the hand-off/reclaim actions themselves.
  const [standinsByRegion, setStandinsByRegion] = useState({});
  const [isLoadingStandins, setIsLoadingStandins] = useState({});
  const [handoffTargetByRegion, setHandoffTargetByRegion] = useState({});
  const [isHandingOff, setIsHandingOff] = useState(null);
  const [isReclaiming, setIsReclaiming] = useState(null);

  // (8 Sep 2026, screen (d)) Current GA envelope per region — max_gas /
  // default_ga_cap as last set by the SA. Displayed so the request form
  // below always shows a "currently: X" baseline instead of asking the RA
  // to change a number they can't see. null fields mean the SA hasn't set
  // an envelope for this region yet (unenforced, per authorityRoutes.js).
  const [envelopeByRegion, setEnvelopeByRegion] = useState({});
  const [isLoadingEnvelope, setIsLoadingEnvelope] = useState({});

  const refetchGaList = (regionId) => {
    setIsLoadingGaList((prev) => ({ ...prev, [regionId]: true }));
    fetch(`${apiBaseUrl}/api/regions/${regionId}/group-admins`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setGaListByRegion((prev) => ({ ...prev, [regionId]: data })))
      .catch(() => setGaListByRegion((prev) => ({ ...prev, [regionId]: [] })))
      .finally(() => setIsLoadingGaList((prev) => ({ ...prev, [regionId]: false })));
  };

  const refetchStandins = (regionId) => {
    setIsLoadingStandins((prev) => ({ ...prev, [regionId]: true }));
    fetch(`${apiBaseUrl}/api/regions/${regionId}/ra-standins`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setStandinsByRegion((prev) => ({ ...prev, [regionId]: data })))
      .catch(() => setStandinsByRegion((prev) => ({ ...prev, [regionId]: [] })))
      .finally(() => setIsLoadingStandins((prev) => ({ ...prev, [regionId]: false })));
  };

  const refetchEnvelope = (regionId) => {
    setIsLoadingEnvelope((prev) => ({ ...prev, [regionId]: true }));
    fetch(`${apiBaseUrl}/api/regions/${regionId}/ga-envelope`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then((res) => (res.ok ? res.json() : { maxGas: null, defaultGaCap: null }))
      .then((data) => setEnvelopeByRegion((prev) => ({ ...prev, [regionId]: data })))
      .catch(() => setEnvelopeByRegion((prev) => ({ ...prev, [regionId]: { maxGas: null, defaultGaCap: null } })))
      .finally(() => setIsLoadingEnvelope((prev) => ({ ...prev, [regionId]: false })));
  };

  const handleHandOff = async (regionId) => {
    const standinUserId = handoffTargetByRegion[regionId];
    if (!standinUserId) {
      onStatusMessage('Please select a stand-in', 'error');
      return;
    }
    setIsHandingOff(regionId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/regions/${regionId}/hand-off-authority`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ standin_user_id: standinUserId })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Authority handed off', 'success');
        window.location.reload();
      } else {
        onStatusMessage(data.error || 'Failed to hand off authority', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to hand off authority', 'error');
    } finally {
      setIsHandingOff(null);
    }
  };

  const handleReclaim = async (regionId) => {
    setIsReclaiming(regionId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/regions/${regionId}/reclaim-authority`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Authority reclaimed', 'success');
        window.location.reload();
      } else {
        onStatusMessage(data.error || 'Failed to reclaim authority', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to reclaim authority', 'error');
    } finally {
      setIsReclaiming(null);
    }
  };

  useEffect(() => {
    (raRegions || []).forEach((region) => {
      if (membersByRegion[region.id] || isLoadingMembers[region.id]) return;
      setIsLoadingMembers((prev) => ({ ...prev, [region.id]: true }));
      fetch(`${apiBaseUrl}/api/regions/${region.id}/members`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => setMembersByRegion((prev) => ({ ...prev, [region.id]: data })))
        .catch(() => setMembersByRegion((prev) => ({ ...prev, [region.id]: [] })))
        .finally(() => setIsLoadingMembers((prev) => ({ ...prev, [region.id]: false })));

      if (!gaListByRegion[region.id] && !isLoadingGaList[region.id]) {
        refetchGaList(region.id);
      }
      if (!standinsByRegion[region.id] && !isLoadingStandins[region.id]) {
        refetchStandins(region.id);
      }
      if (!envelopeByRegion[region.id] && !isLoadingEnvelope[region.id]) {
        refetchEnvelope(region.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raRegions, apiBaseUrl, authToken]);

  const handleAppointGa = async (regionId) => {
    const typed = (gaTypedByRegion[regionId] || '').trim();
    const email = typed || (gaEmailByRegion[regionId] || '').trim();
    const cap = Number(gaCapByRegion[regionId]) || 1;
    if (!email) {
      onStatusMessage('Please choose a member or type an email address.', 'error');
      return;
    }
    setIsAppointingGa(regionId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/regions/${regionId}/appoint-ga`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, max_calendars: cap })
      });
      const data = await res.json();
      if (res.ok) {
        // (28 Oct 2026) Long enough to read whether they were emailed.
        onStatusMessage(data.message || 'Group Admin appointed', 'success', 'long');
        setGaEmailByRegion((prev) => ({ ...prev, [regionId]: '' }));
        setGaTypedByRegion((prev) => ({ ...prev, [regionId]: '' }));
        // (2 Nov 2026, decision 171) Back to 1, so an Edit's cap is not carried into the next appointment.
        setGaCapByRegion((prev) => ({ ...prev, [regionId]: 1 }));
        refetchGaList(regionId);
      } else {
        onStatusMessage(data.error || 'Failed to appoint Group Admin', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to appoint Group Admin', 'error');
    } finally {
      setIsAppointingGa(null);
    }
  };

  // (28 Oct 2026, decision 151) Remove a Group Admin who owns no calendars.
  const handleRemoveGa = async (regionId, ga) => {
    setIsRemovingGa(ga.roleAssignmentId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/regions/${regionId}/group-admins/${ga.userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onStatusMessage(data.message || 'Group Admin removed', 'success');
        setRemoveAskId(null);
        refetchGaList(regionId);
      } else {
        onStatusMessage(data.error || 'Failed to remove Group Admin', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to remove Group Admin', 'error');
    } finally {
      setIsRemovingGa(null);
    }
  };

  // --- Appoint RA stand-in (base RA only) ---
  const [standinEmailByRegion, setStandinEmailByRegion] = useState({});
  const [standinStartByRegion, setStandinStartByRegion] = useState({});
  const [standinEndByRegion, setStandinEndByRegion] = useState({});
  const [isAppointingStandin, setIsAppointingStandin] = useState(null);

  const handleAppointStandin = async (regionId) => {
    const email = (standinEmailByRegion[regionId] || '').trim();
    const startDate = standinStartByRegion[regionId] || '';
    const endDate = standinEndByRegion[regionId] || '';
    if (!email || !startDate || !endDate) {
      onStatusMessage('Please enter an email and both dates', 'error');
      return;
    }
    setIsAppointingStandin(regionId);
    try {
      const lookupRes = await fetch(`${apiBaseUrl}/api/users/lookup?email=${encodeURIComponent(email)}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const lookupData = await lookupRes.json();
      if (!lookupRes.ok) {
        onStatusMessage(lookupData.error || 'Failed to look up that account', 'error');
        return;
      }

      const res = await fetch(`${apiBaseUrl}/api/regions/${regionId}/ra-standins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: lookupData.id, start_date: startDate, end_date: endDate })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'RA stand-in appointed', 'success');
        setStandinEmailByRegion((prev) => ({ ...prev, [regionId]: '' }));
        setStandinStartByRegion((prev) => ({ ...prev, [regionId]: '' }));
        setStandinEndByRegion((prev) => ({ ...prev, [regionId]: '' }));
      } else {
        onStatusMessage(data.error || 'Failed to appoint RA stand-in', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to appoint RA stand-in', 'error');
    } finally {
      setIsAppointingStandin(null);
    }
  };

  // --- Request GA envelope increase: base-RA side (screen d prerequisite,
  // 8 Sep). appoint-ga/raise-cap already surface a clear 409 message when
  // an action would exceed the RA's envelope (see authorityRoutes.js) —
  // this form is the separate, deliberate action to ask the SA to raise it.
  const [envelopeMaxGasByRegion, setEnvelopeMaxGasByRegion] = useState({});
  const [envelopeCapByRegion, setEnvelopeCapByRegion] = useState({});
  const [isRequestingIncrease, setIsRequestingIncrease] = useState(null);

  const handleRequestIncrease = async (regionId) => {
    const maxGas = envelopeMaxGasByRegion[regionId];
    const defaultCap = envelopeCapByRegion[regionId];
    if (!maxGas && !defaultCap) {
      onStatusMessage('Enter a requested value for max Group Admins and/or default cap', 'error');
      return;
    }
    setIsRequestingIncrease(regionId);
    try {
      const body = {};
      if (maxGas) body.requested_max_gas = Number(maxGas);
      if (defaultCap) body.requested_default_ga_cap = Number(defaultCap);

      const res = await fetch(`${apiBaseUrl}/api/regions/${regionId}/request-ga-envelope-increase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Envelope increase requested', 'success');
        setEnvelopeMaxGasByRegion((prev) => ({ ...prev, [regionId]: '' }));
        setEnvelopeCapByRegion((prev) => ({ ...prev, [regionId]: '' }));
      } else {
        onStatusMessage(data.error || 'Failed to request envelope increase', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to request envelope increase', 'error');
    } finally {
      setIsRequestingIncrease(null);
    }
  };

  const hasAnyAuthority = raRegions && raRegions.length > 0;

  return (
    <div className="space-y-6">
      {!hasAnyAuthority && (
        <p className="text-sm text-gray-500">You don't hold Regional Authority for any region.</p>
      )}

      {raRegions && raRegions.map((region) => (
        <div key={region.id}>
          <h3 className="text-lg font-semibold mb-1">{region.name}</h3>
          <p className="text-xs text-gray-500 mb-1">You are {ROLE_LABEL[region.role] || region.role} for this region.</p>
          <p className="text-xs text-gray-500 mb-3">
            {region.isActiveHolder
              ? 'You currently hold active authority for this region.'
              : `Authority is currently held by ${region.activeHolderName || 'someone else'} — actions below are unavailable until you reclaim it.`}
          </p>

          <div className="border border-gray-200 rounded-lg p-3 space-y-2 mb-3">
            <p className="text-sm font-medium text-gray-700">Authority</p>
            {region.isActiveHolder ? (
              <>
                {isLoadingStandins[region.id] ? (
                  <p className="text-xs text-gray-400">Loading stand-ins…</p>
                ) : (standinsByRegion[region.id] || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No stand-ins appointed yet.</p>
                ) : (
                  <>
                    <select
                      value={handoffTargetByRegion[region.id] || ''}
                      onChange={(e) => setHandoffTargetByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                      className={`${TOUCH} w-full border border-gray-300 rounded-lg px-3 py-2 text-sm`}
                    >
                      <option value="">Hand off to…</option>
                      {(standinsByRegion[region.id] || []).map((s) => (
                        <option key={s.roleAssignmentId} value={s.userId} disabled={!s.isCurrentlyEligible}>
                          {(s.preferredName || s.email)}{!s.isCurrentlyEligible ? ' (window not active)' : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleHandOff(region.id)}
                      disabled={isHandingOff === region.id}
                      className={`${TOUCH} w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition disabled:opacity-50`}
                    >
                      {isHandingOff === region.id ? 'Handing off…' : 'Hand Off Authority'}
                    </button>
                  </>
                )}
              </>
            ) : region.role === 'ra' ? (
              <button
                onClick={() => handleReclaim(region.id)}
                disabled={isReclaiming === region.id}
                className={`${TOUCH} w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition disabled:opacity-50`}
              >
                {isReclaiming === region.id ? 'Reclaiming…' : 'Reclaim Authority'}
              </button>
            ) : (
              <p className="text-xs text-gray-400 italic">
                You're an appointed stand-in, but authority hasn't been handed to you yet.
              </p>
            )}
          </div>

          <div className={`border border-gray-200 rounded-lg p-3 space-y-2 mb-3 ${!region.isActiveHolder ? 'opacity-50' : ''}`}>
            <p className="text-sm font-medium text-gray-700">Current Group Admins</p>
            {isLoadingGaList[region.id] ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : (gaListByRegion[region.id] || []).length === 0 ? (
              <p className="text-xs text-gray-400 italic">No Group Admin appointed yet.</p>
            ) : (
              // (2 Nov 2026, decision 172) The name on its own line and the buttons below
              // it, separated from the next Group Admin by a rule (phones and wider screens alike).
              <ul className="divide-y divide-gray-100">
                {(gaListByRegion[region.id] || []).map((ga) => (
                  <li key={ga.roleAssignmentId} className="flex flex-col gap-1.5 py-2 first:pt-0 last:pb-0 text-sm">
                    <span className="text-gray-700 break-words">
                      {ga.preferredName
                        ? <>{ga.preferredName} <span className="text-gray-400">({ga.email})</span></>
                        : ga.email}
                      {/* (28 Oct 2026, decision 151) Appointed by email, not signed in yet. */}
                      {ga.signedIn === false && (
                        <span className="text-gray-400 italic"> (not yet signed in)</span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => {
                          // (28 Oct 2026) Into the typed box, which works whether or not
                          // they are in the member picker.
                          setGaTypedByRegion((prev) => ({ ...prev, [region.id]: ga.email }));
                          setGaEmailByRegion((prev) => ({ ...prev, [region.id]: '' }));
                          setGaCapByRegion((prev) => ({ ...prev, [region.id]: ga.cap }));
                        }}
                        className={`${GA_BTN} border-blue-300 text-blue-700 hover:bg-blue-50`}
                      >
                        Edit cap ({ga.cap})
                      </button>
                      {/* (28 Oct 2026, decision 151) Only for a Group Admin who owns no
                          calendars; "Keep?" first, then "Remove?" (decision 130). */}
                      {region.isActiveHolder && ga.calendarCount === 0 && (
                        removeAskId === ga.roleAssignmentId ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setRemoveAskId(null)}
                              disabled={isRemovingGa === ga.roleAssignmentId}
                              className={`${GA_BTN} border-gray-300 text-gray-700 hover:bg-gray-50`}
                            >
                              Keep?
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveGa(region.id, ga)}
                              disabled={isRemovingGa === ga.roleAssignmentId}
                              className={`${GA_BTN} border-red-300 text-red-600 hover:bg-red-50`}
                            >
                              {isRemovingGa === ga.roleAssignmentId ? 'Removing…' : 'Remove?'}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setRemoveAskId(ga.roleAssignmentId)}
                            className={`${GA_BTN} border-red-300 text-red-600 hover:bg-red-50`}
                          >
                            Remove
                          </button>
                        )
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={`border border-gray-200 rounded-lg p-3 space-y-2 mb-3 ${!region.isActiveHolder ? 'opacity-50' : ''}`}>
            <p className="text-sm font-medium text-gray-700">Appoint Group Admin / set cap</p>
            {isLoadingMembers[region.id] ? (
              <p className="text-xs text-gray-400">Loading members…</p>
            ) : (membersByRegion[region.id] || []).length === 0 ? (
              <p className="text-xs text-gray-400 italic">No members found in this region's calendars yet.</p>
            ) : (
              <select
                value={gaEmailByRegion[region.id] || ''}
                onChange={(e) => setGaEmailByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                className={`${TOUCH} w-full border border-gray-300 rounded-lg px-3 py-2 text-sm`}
              >
                <option value="">Select a member…</option>
                {(membersByRegion[region.id] || []).map((m) => (
                  <option key={m.id} value={m.email}>
                    {m.preferred_name ? `${m.preferred_name} — ${m.email}` : m.email}
                  </option>
                ))}
              </select>
            )}
            {/* (28 Oct 2026, decision 151) Anyone, by email address: used instead of
                the choice above when filled in. */}
            <input
              type="email"
              autoComplete="off"
              value={gaTypedByRegion[region.id] || ''}
              onChange={(e) => setGaTypedByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
              placeholder="or type an email address"
              className={`${TOUCH} w-full border border-gray-300 rounded-lg px-3 py-2 text-sm`}
            />
            <p className="text-xs text-gray-500">
              Someone new to Calendar Hub can be appointed by email address. They are emailed, and can then sign in and create their calendar.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">Calendar cap</label>
              <select
                value={gaCapByRegion[region.id] || 1}
                onChange={(e) => setGaCapByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                className={`${TOUCH} border border-gray-300 rounded-lg px-2 py-1.5 text-sm`}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>
            <button
              onClick={() => handleAppointGa(region.id)}
              disabled={isAppointingGa === region.id || !region.isActiveHolder}
              className={`${TOUCH} w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50`}
            >
              {isAppointingGa === region.id ? 'Appointing…' : 'Appoint / Update'}
            </button>
          </div>

          {region.role === 'ra' && (
            <div className={`border border-gray-200 rounded-lg p-3 space-y-2 mb-3 ${!region.isActiveHolder ? 'opacity-50' : ''}`}>
              <p className="text-sm font-medium text-gray-700">Appoint RA stand-in (holiday cover)</p>
              <input
                type="email"
                autoComplete="off"
                value={standinEmailByRegion[region.id] || ''}
                onChange={(e) => setStandinEmailByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                placeholder="Stand-in's email"
                className={`${TOUCH} w-full border border-gray-300 rounded-lg px-3 py-2 text-sm`}
              />
              <div className="flex gap-2">
                <input
                  type="date"
                  value={standinStartByRegion[region.id] || ''}
                  onChange={(e) => setStandinStartByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                  className={`${TOUCH} flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm`}
                />
                <input
                  type="date"
                  value={standinEndByRegion[region.id] || ''}
                  onChange={(e) => setStandinEndByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                  className={`${TOUCH} flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm`}
                />
              </div>
              <button
                onClick={() => handleAppointStandin(region.id)}
                disabled={isAppointingStandin === region.id || !region.isActiveHolder}
                className={`${TOUCH} w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50`}
              >
                {isAppointingStandin === region.id ? 'Appointing…' : 'Appoint Stand-in'}
              </button>
            </div>
          )}

          {region.role === 'ra' && (
            <div className={`border border-gray-200 rounded-lg p-3 space-y-2 ${!region.isActiveHolder ? 'opacity-50' : ''}`}>
              <p className="text-sm font-medium text-gray-700">Request GA envelope increase</p>
              <p className="text-xs text-gray-500 mb-1">
                {isLoadingEnvelope[region.id] ? (
                  'Loading current envelope…'
                ) : (
                  <>
                    Currently:{' '}
                    {envelopeByRegion[region.id]?.maxGas != null
                      ? `max ${envelopeByRegion[region.id].maxGas} Group Admin${envelopeByRegion[region.id].maxGas === 1 ? '' : 's'}`
                      : 'no max set'}
                    {' · '}
                    {envelopeByRegion[region.id]?.defaultGaCap != null
                      ? `cap ceiling ${envelopeByRegion[region.id].defaultGaCap}`
                      : 'no cap ceiling set'}
                  </>
                )}
              </p>
              <p className="text-xs text-gray-500">
                If appointing or capping a Group Admin was refused for exceeding your delegated limit, request a higher one here — the System Authority will review it.
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Max Group Admins</label>
                <input
                  type="number"
                  min="1"
                  value={envelopeMaxGasByRegion[region.id] || ''}
                  onChange={(e) => setEnvelopeMaxGasByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                  placeholder="e.g. 5"
                  className={`${TOUCH} flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-sm`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Default GA cap</label>
                <input
                  type="number"
                  min="1"
                  value={envelopeCapByRegion[region.id] || ''}
                  onChange={(e) => setEnvelopeCapByRegion((prev) => ({ ...prev, [region.id]: e.target.value }))}
                  placeholder="e.g. 3"
                  className={`${TOUCH} flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-sm`}
                />
              </div>
              <button
                onClick={() => handleRequestIncrease(region.id)}
                disabled={isRequestingIncrease === region.id || !region.isActiveHolder}
                className={`${TOUCH} w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition disabled:opacity-50`}
              >
                {isRequestingIncrease === region.id ? 'Requesting…' : 'Request Increase'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RegionalAuthorityTab;
